import type { ArtefactSummary } from "@esi/shared";
import { BookPlus, Download, PanelRight } from "lucide-react";
import { type CSSProperties, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { formatDate } from "../../i18n/format.ts";
import { AddToKnowledgeDialog } from "./AddToKnowledgeDialog.tsx";
import { KindIcon } from "./kind.tsx";
import { artefactDownloadHref, type ConversationScope } from "./queries.ts";

/**
 * One artefact in the knowledge tree's Artefacts section (design §7): kind, title, "v3" and when
 * it was updated; the tree row opens it beside the conversation (Enter, or a click), and its
 * menu offers Open, Download and, for uploaders, "Add to knowledge" (artefacts-to-knowledge),
 * whose dialog turns into an update when an earlier version is there. Versions are not child
 * nodes: the panel's switcher has them.
 */
export function ArtefactRow(props: {
  artefact: ArtefactSummary;
  scope: ConversationScope;
  style: CSSProperties;
  /** The title, with the search match marked. */
  name: ReactNode;
  nameId: string;
  detailsId: string;
  /** Wraps the menu items in the tree's row menu. */
  menu: (children: ReactNode) => ReactNode;
  onOpen: () => void;
  /** The viewer may upload to the conversation's project. */
  canAdd?: boolean;
  /** Where focus goes when the dialog closes (the row). */
  returnFocus?: () => HTMLElement | null;
}) {
  const { artefact: a } = props;
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  return (
    <span
      data-esi-content="files"
      className="flex items-start gap-1 py-2 pr-1.5 text-sm"
      style={props.style}
    >
      <button
        type="button"
        tabIndex={-1}
        onClick={props.onOpen}
        className="flex min-w-0 flex-1 flex-col gap-1 text-left"
      >
        <span className="flex min-w-0 items-start font-medium break-words text-fg">
          <KindIcon kind={a.kind} className="mt-0.5 mr-1.5 text-fg-muted" />
          <span className="sr-only">{t("knowledgePanel.type.artefact")}: </span>
          <span id={props.nameId} className="min-w-0">
            {props.name}
          </span>
        </span>
        <span id={props.detailsId} className="text-xs text-fg-muted tabular-nums">
          {[
            t(`artefacts.kind.${a.kind}`),
            t("artefacts.version", { version: a.version }),
            t("artefacts.section.updated", { date: formatDate(a.updatedAt) }),
          ].join(" · ")}
        </span>
      </button>
      {props.menu(
        <>
          <DropdownMenuItem onSelect={props.onOpen}>
            <PanelRight aria-hidden="true" strokeWidth={1.75} />
            {t("artefacts.open")}
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={artefactDownloadHref(props.scope, a.id, a.version, a.kind)} download>
              <Download aria-hidden="true" strokeWidth={1.75} />
              {t("artefacts.panel.download")}
            </a>
          </DropdownMenuItem>
          {props.canAdd ? (
            <DropdownMenuItem onSelect={() => setAdding(true)}>
              <BookPlus aria-hidden="true" strokeWidth={1.75} />
              {t("artefactKnowledge.addEllipsis")}
            </DropdownMenuItem>
          ) : null}
        </>,
      )}
      {adding ? (
        <AddToKnowledgeDialog
          open
          onClose={() => setAdding(false)}
          scope={props.scope}
          artefactId={a.id}
          version={a.version}
          title={a.title}
          returnFocus={props.returnFocus}
        />
      ) : null}
    </span>
  );
}
